import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Tag, Calendar, Radio, Hash, Eye, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getChannelColor = (channel) => {
  const colors = {
    'Google Ads': 'bg-blue-100 text-blue-700',
    'Facebook Ads': 'bg-indigo-100 text-indigo-700',
    'Instagram': 'bg-pink-100 text-pink-700',
    'WhatsApp': 'bg-green-100 text-green-700',
    'LinkedIn': 'bg-sky-100 text-sky-700',
    'Email Marketing': 'bg-amber-100 text-amber-700',
  };
  return colors[channel] || 'bg-slate-100 text-slate-600';
};

export default function MiCampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/campaigns/mis-campanas/${campaignId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCampaign(data.campaign);
      setLeads(data.leads);
    } catch {
      toast.error('Error al cargar campaña');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Campaña no encontrada</p>
        <button onClick={() => navigate('/campaigns/mis-campanas')} className="text-indigo-600 hover:underline mt-2">Volver</button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/campaigns/mis-campanas')} className="p-2 hover:bg-slate-100 rounded-lg mt-1" data-testid="back-to-campaigns">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-mono">
              <Hash className="w-3 h-3" /> {campaign.tag}
            </span>
            <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getChannelColor(campaign.channel)}`}>
              <Radio className="w-3 h-3 mr-1" /> {campaign.channel}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatDate(campaign.createdAt)}
            </span>
            <span className="text-xs text-slate-500">Por: {campaign.createdByName}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="campaign-leads-count">{leads.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg"><Tag className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Tag</p>
              <p className="text-lg font-mono font-bold text-indigo-600">{campaign.tag}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><Radio className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Canal</p>
              <p className="text-lg font-bold text-green-600">{campaign.channel}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">Leads Registrados ({leads.length})</h2>
        </div>
        {leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay leads registrados en esta campaña</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Lead</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contacto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Embudo / Etapa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3"><p className="font-medium text-slate-900">{lead.name}</p></td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        {lead.email && <p className="text-sm text-slate-600 flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {lead.email}</p>}
                        {lead.phone && <p className="text-sm text-slate-600 flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {lead.phone}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lead.stageColor || '#6366f1' }}></span>
                        <div>
                          <p className="text-sm text-slate-700">{lead.pipelineName || '-'}</p>
                          <p className="text-xs text-slate-500">{lead.stageName || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatDate(lead.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => navigate(`/lead/${lead.id}`)} className="p-2 hover:bg-blue-50 rounded-lg text-slate-400 hover:text-blue-600">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
